import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Button from '../ui/Button';

interface LicenseEnforcementModalProps {
  action: 'suspend' | 'terminate';
  onSubmit: (reasons: { internal: string; public: string }) => Promise<void>;
  onClose: () => void;
}

const PUBLIC_REASON_LIMIT = 500;

const PUBLIC_REASON_PLACEHOLDER = 'e.g. "Material breach of clause 4.2 — unauthorised propagation of sublicensed cultivar."';

const INTERNAL_REASON_PLACEHOLDER = `Full audit detail — what happened, when it was discovered, evidence, prior warnings, decision rationale, who was consulted.

This text is retained in the licence audit log and is NEVER sent to the licensee.`;

/**
 * Modal for capturing the dual reason record required to suspend or terminate
 * a licence. The internal reason is verbose and audit-only; the public reason
 * is brief, hard-capped at 500 characters, and is the only text the licensee
 * sees on screen and in the notification email.
 */
function LicenseEnforcementModal({ action, onSubmit, onClose }: LicenseEnforcementModalProps) {
  const [internal, setInternal] = useState('');
  const [publicReason, setPublicReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verb = action === 'suspend' ? 'Suspend' : 'Terminate';
  const accent = action === 'suspend' ? 'amber' : 'red';

  const canSubmit =
    internal.trim().length > 0 &&
    publicReason.trim().length > 0 &&
    publicReason.length <= PUBLIC_REASON_LIMIT &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ internal: internal.trim(), public: publicReason.trim() });
    } catch (e: any) {
      setError(e?.message || `${verb} failed`);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className={`px-6 py-4 border-b border-neutral-200 bg-${accent}-50 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full bg-${accent}-100 flex items-center justify-center`}>
              <AlertTriangle className={`h-5 w-5 text-${accent}-700`} />
            </div>
            <div>
              <h2 className={`text-lg font-bold text-${accent}-900`}>{verb} licence</h2>
              <p className={`text-xs text-${accent}-700`}>
                {action === 'terminate'
                  ? 'This action is irreversible.'
                  : 'The licensee may not propagate, sell, or plant under this licence while suspended.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-5">
          {/* Public reason */}
          <div>
            <label className="block text-sm font-semibold text-neutral-800 mb-1">
              Reason shown to licensee <span className="text-red-600">*</span>
            </label>
            <p className="text-xs text-neutral-500 mb-2">
              Brief — appears on the licensee's agreement page and in the email
              notification. Suggested phrasing: a short reference to the clause
              breached. Max {PUBLIC_REASON_LIMIT} characters.
            </p>
            <textarea
              value={publicReason}
              onChange={e => setPublicReason(e.target.value)}
              placeholder={PUBLIC_REASON_PLACEHOLDER}
              rows={3}
              maxLength={PUBLIC_REASON_LIMIT}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-neutral-400 text-right mt-1">
              {publicReason.length}/{PUBLIC_REASON_LIMIT}
            </p>
          </div>

          {/* Internal reason */}
          <div>
            <label className="block text-sm font-semibold text-neutral-800 mb-1">
              Internal audit record <span className="text-red-600">*</span>
            </label>
            <p className="text-xs text-neutral-500 mb-2">
              Full detail for the audit log. Not visible to the licensee. Include
              what happened, when, evidence, prior warnings, and who decided.
            </p>
            <textarea
              value={internal}
              onChange={e => setInternal(e.target.value)}
              placeholder={INTERNAL_REASON_PLACEHOLDER}
              rows={8}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
            />
          </div>

          <div className={`bg-${accent}-50 border border-${accent}-200 rounded p-3 text-xs text-${accent}-900`}>
            <strong>On submit:</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li>The licence status changes to <strong>{action === 'suspend' ? 'suspended' : 'terminated'}</strong>.</li>
              <li>An email (with the reason above) is sent to the licensee.</li>
              <li>An email is sent to every cultivar admin in your organisation.</li>
              <li>Both reasons are written to the cultivar audit log permanently.</li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={action === 'terminate' ? 'danger' : 'primary'}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting
              ? `${verb}ing…`
              : `${verb} licence`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LicenseEnforcementModal;
